import {BaseService} from "@/api/baseService";

export interface PostCategory<DateType = string> {
    createdAt: DateType;
    id: string;
    title: string;
    slug: string;
    updatedAt: DateType;
}

export interface PostAuthor<DateType = string> {
    id?: string;
    slug: string;
    name: string;
    title?: string;
    bio?: string;
    photoUrl?: string;
    socialUrls?: string[];
    createdAt?: DateType;
    updatedAt?: DateType;
}

export interface Post<DateType = string> {
    createdAt: DateType;
    id: string;
    title: string;
    slug: string;
    content: string;
    postCategory: PostCategory<DateType>;
    updatedAt: DateType;
    featuredImage: string;
    publishDate: DateType;
    isVisible: boolean;
    annotation?: string;
    showInLatestNews: boolean;

    videos: IPostVideo[]
    contentRow: IContentRow[]

    author?: PostAuthor<DateType>;
    mentions?: PostAuthor<DateType>[];
    tags?: PostTag<DateType>[];

    podcastSeriesName?: string;
    podcastSeriesUrl?: string;
    podcastAudioUrl?: string;
    podcastEmbedUrl?: string;
    podcastDuration?: string;
    podcastGuestName?: string;
    podcastGuestUrl?: string;
    podcastEpisodeNumber?: number;
    podcastSeasonNumber?: number;
}

export interface PostTag<DateType = string> {
    id?: string;
    slug: string;
    name: string;
    createdAt?: DateType;
    updatedAt?: DateType;
}

export interface IPostVideo {
    id: string;
    image: string;
}

export interface IContentRow {
    type: IContentRowType;
    value?: string;
    id?: string;
    image?: string
}

export type IContentRowType = 'text' | 'video' | 'form';

export interface PostFull<DateType = string> extends Omit<Post<DateType>, "postCategory">
{
    category?: PostCategory<DateType>;
}

export class PostService extends BaseService {
    public static async getCategories(): Promise<PostCategory[]> {
        const response = await this.getData<PostCategory[]>('/postCategories');
        return response;
    }

    public static async getCategoryBySlug(slug: string): Promise<PostCategory | undefined> {
        const categories = await this.getCategories();
        return categories.find(category => category.slug === slug);
    }

    public static async getCategoryById(id: string): Promise<PostCategory | undefined> {
        const categories = await this.getCategories();
        return categories.find(category => category.id === id);
    }

    public static async get(): Promise<PostCategory[]> {
        const response = await this.getData<PostCategory[]>('/posts');
        return response;
    }

    private static async getByParams(params?: any): Promise<Post[]> {
        const response = await this.getData<Post[]>('/posts', params);
        return response as Post[];
    }

    public static async getBySlug(slug: string): Promise<Post | undefined> {
        const result = await this.getByParams({
            where: {
                slug: {
                    equals: slug
                },
                isVisible: {
                    equals: true
                }
            }
        });
        if(result === undefined) {
            return undefined;
        }

        const post = result.pop();
        if (!post?.content) return post;

        let contentRow: IContentRow[] = [];
        let videoMatches = Array.from(post.content.matchAll(/\[VIDEO\?Id=(\d+)\?Image="([^"]+)"\]/g));
        let contactFormMatch = post.content.includes('[CONTACT_FORM]');

        let content = post.content;
        let videos: { id: string; image: string }[] = [];

        for (const match of videoMatches) {
            const [fullMatch, videoId, videoImage] = match;
            const [before, after] = content.split(fullMatch);

            if (before.trim()) contentRow.push({ type: 'text', value: before.trim() });
            contentRow.push({ type: 'video', id: videoId, image: videoImage });

            videos.push({ id: videoId, image: videoImage });
            content = after;
        }

        if (contactFormMatch) {
            const [beforeForm, afterForm] = content.split('[CONTACT_FORM]');

            if (beforeForm.trim()) contentRow.push({ type: 'text', value: beforeForm.trim() });
            contentRow.push({ type: 'form' });
            if (afterForm.trim()) contentRow.push({ type: 'text', value: afterForm.trim() });
        } else {
            if (content.trim()) contentRow.push({ type: 'text', value: content.trim() });
        }

        return {
            ...post,
            videos,
            contentRow,
        };
    }


    public static async getByAuthorSlug(authorSlug: string, limit?: number): Promise<Post[]> {
        const params: Record<string, unknown> = {
            where: {
                author: {
                    slug: {
                        equals: authorSlug
                    }
                },
                isVisible: {
                    equals: true
                }
            },
            orderBy: {
                publishDate: 'desc'
            }
        };
        if (limit !== undefined) {
            (params as Record<string, unknown>)["take"] = limit;
        }
        const result = await this.getByParams(params);
        return result ?? [];
    }

    public static async getByCategorySlug(slug: string): Promise<Post[] | undefined> {
        const category = await this.getCategoryBySlug(slug);
        if(category === undefined) {
            return undefined;
        }
        return this.getByParams({
            where: {
                postCategory: {
                    id: {
                        equals: category.id
                    }
                },
                isVisible: {
                    equals: true
                }
            },
            orderBy: {
                publishDate: 'desc'
            }
        });
    }

    public static async getPosts(): Promise<Post[]> {
        return this.getByParams({
            where: {
                isVisible: {
                    equals: true
                }
            },
            orderBy: {
                publishDate: 'desc'
            }
        });
    }

    private static toFull(post: Post, categories: PostCategory[]): PostFull
    {
        const categoryId = post.postCategory?.id;
        const category = categoryId !== undefined ? categories.find(category => category.id === categoryId) : undefined;

        return { ...post, category };
    }

    public static async getFull(): Promise<{ categories: PostCategory[]; posts: PostFull[] }>
    {
        const [allPosts, allCategories] = await Promise.all([
            this.getPosts(),
            this.getCategories(),
        ]);

        const posts = allPosts.map(post => this.toFull(post, allCategories)).sort((a, b) =>
          new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());
        const categories = allCategories.filter(category => posts.some(post => post.category?.id === category.id));

        return {
            categories, posts,
        };
    }
}